// RODAPÉ "Horizonte" (rodape = 8): rodapé do modelo "Horizonte" (cinematográfico/editorial).
//  - CHAMADA FINAL em tipografia grande com texto em DEGRADÊ ANIMADO (dois degradês da paleta
//    do tenant se alternando por opacidade — só compositor) + botões WhatsApp/telefone e
//    "Ver imóveis";
//  - FAIXA CONTÍNUA (marquee) com as cidades e os tipos de imóvel do tenant, cada um link para
//    a listagem; corre em CSS (transform) e PAUSA fora da tela (useInView), no hover e no foco;
//  - grade de 4 colunas que se REVELAM em cascata ao entrar na tela (whileInView, once):
//    marca/CRECI/redes · navegação · contato (e-mail revelado no clique) · institucional;
//  - filiais (FiliaisRodape), linha final com direitos, CRECI/CNPJ, html_rodape e selo
//    ImobTotal (gate dentro do componente), e botão "voltar ao topo" MAGNÉTICO (segue o mouse
//    com mola).
// Fundos: cor3 (conteúdo) + cor6 (linha final), texto cor7 com contraste CALCULADO.
// prefers-reduced-motion: sem marquee (lista estática), sem degradê animado, sem ímã.
// "Pesquisas mais populares" como os demais rodapés (hidePopular). Default export =
// ErrorBoundary com fallback (padrão Aurora/Autoral).
import { motion, MotionConfig, useInView, useMotionValue, useSpring } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, getContrastRatio, isColorLight, MaskPhone, removerAcentos } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import type { RodapeSectionProps } from '../../registry/types';
import { encodeSegment } from '../../shared/hero-search/useHeroSearch';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import { contatosDoTenant, EASE_HORIZONTE, hexParaRgba, linkWhatsAppDoTenant, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
import type { ItemMenu } from '../horizonte/horizonte';
import Popular from '../populares/Popular';
import LinhaSeoRodape from '../rodape-seo/LinhaSeoRodape';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';
import hzHome from '../horizonte-secoes/HorizonteHome.module.css';
import styles from './HorizonteFooter.module.css';

// Páginas do tenant que são "institucionais" (coluna Institucional, não Navegação).
const INSTITUCIONAL_RE = /quem somos|sobre n|a empresa|nossa historia|institucional|trabalhe|privacidade|lgpd|termos/;
const ehInstitucional = (titulo: unknown): boolean =>
    INSTITUCIONAL_RE.test(removerAcentos(String(titulo ?? '')).toLowerCase().replace(/\s+/g, ' '));
const ehLegal = (titulo: unknown): boolean => /privacidade|lgpd|termos/i.test(String(titulo ?? ''));

const ICONE_REDE: Record<string, React.ComponentType<{ 'aria-hidden'?: boolean }>> = {
    Instagram: FaInstagram,
    Facebook: FaFacebook,
    Youtube: FaYoutube,
    Tiktok: FaTiktok,
    Linkedin: FaLinkedin,
};

const FRASE_CHAMADA = 'Seu próximo endereço começa aqui.';

const IconeSeta = ({ cima = false }: { cima?: boolean }) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {cima ? (
            <>
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="6 11 12 5 18 11" />
            </>
        ) : (
            <>
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
            </>
        )}
    </svg>
);

type ItemFaixa = { label: string; href: string; tipo: 'cidade' | 'tipo' };

const HorizonteFooterInner: React.FC<RodapeSectionProps> = ({ site: siteProp, origin, codsite, allRodape, hidePopular, allCities, allCitiesTipes }) => {
    // MULTI-REGIÃO: contatos/endereço trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const reduzido = useMovimentoReduzido();
    const [isMounted, setIsMounted] = useState(false);
    const [emailVisivel, setEmailVisivel] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    const trackFooter = (nome: string, cat: string) => () => trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // ---- cores: fundo cor3 / linha final cor6; texto cor7 quando contrasta, senão calculado ----
    const corFundo = site?.cor3 || '#0f1219';
    const fundoClaro = isColorLight(corFundo);
    const corTexto = ensureReadableTextColor(site?.cor7 || (fundoClaro ? site?.cor11 : site?.cor13), corFundo) || (fundoClaro ? '#141821' : '#ffffff');
    const corAcento = ensureReadableTextColor(site?.cor2, corFundo, 3.0, corTexto, corTexto) || corTexto;
    const corBarra = site?.cor6 || corFundo;
    const corBarraTexto = ensureReadableTextColor(site?.cor7 || (isColorLight(corBarra) ? site?.cor11 : site?.cor13), corBarra) || corTexto;
    const corCta = site?.cor2 || '#c8a46a';
    const corCtaTexto = ensureReadableTextColor(isColorLight(corCta) ? site?.cor11 : site?.cor13, corCta) || '#ffffff';
    // degradê da chamada: cor2 e cor1 (cada uma só se ler sobre o fundo) + um tom intermediário.
    // Sem contraste, a cor da marca vai sendo puxada para a cor do texto só até ler 3:1 (título
    // grande) — antes caía direto no corTexto e o degradê virava uma cor só (ex.: verde no branco).
    const legivel = (cor: string | undefined | null): string => {
        if (!cor) return corTexto;
        for (let t = 0; t <= 1; t += 0.1) {
            const c = t === 0 ? cor : misturarCores(cor, corTexto, t);
            if (getContrastRatio(c, corFundo) >= 3) return c;
        }
        return corTexto;
    };
    const g1 = legivel(site?.cor2);
    let g2 = site?.cor1 && getContrastRatio(site.cor1, corFundo) >= 3 ? site.cor1 : misturarCores(g1, corTexto, 0.55);
    if (g2.toLowerCase() === g1.toLowerCase()) g2 = misturarCores(g1, corTexto, 0.55);
    const gMeio = misturarCores(g1, g2, 0.5);

    // ---- navegação: Início + transações + menus do tenant (não institucionais) + blog ----
    const menusDisponiveis = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo);
    const paraItem = (m: any): ItemMenu => ({
        label: m.titulo,
        href: m?.link ? m.link : `/menu/${m.titulo}`,
        external: Boolean(m?.link && !String(m.link).startsWith('/')),
    });
    const transacaoPrincipal = String(site?.transacoes?.[0] || 'venda').toLowerCase();
    const navegacao: ItemMenu[] = [{ label: 'Início', href: '/' }];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        navegacao.push({ label: `Imóveis para ${low === 'aluguel' ? 'alugar' : low === 'venda' ? 'comprar' : low}`, href: `/imoveis/${low}/-/-/-/-` });
    });
    menusDisponiveis
        .filter((m: any) => !ehInstitucional(m.titulo))
        .slice(0, 7)
        .forEach((m: any) => navegacao.push(paraItem(m)));
    if (allDataHome?.blogs?.[0]?.id) navegacao.push({ label: 'Blog', href: '/blog' });

    // Política/termos na LINHA FINAL, ao lado do © (Daniel 15/10: "onde estão os termos de uso e política?" — como
    // no Atelier e no Larissa). A coluna Institucional fica só com as páginas do tenant e some quando não há nenhuma.
    // Política/termos padrão da plataforma só quando o tenant não tem a própria página.
    const institucional: ItemMenu[] = menusDisponiveis
        .filter((m: any) => ehInstitucional(m.titulo) && !ehLegal(m.titulo))
        .slice(0, 5)
        .map(paraItem);
    const legais: ItemMenu[] = menusDisponiveis.filter((m: any) => ehLegal(m.titulo)).map(paraItem);
    if (!legais.some((i) => /privacidade|lgpd/i.test(i.label))) legais.push({ label: 'Política de privacidade', href: '/politica' });
    if (!legais.some((i) => /termos/i.test(i.label))) legais.push({ label: 'Termos de uso', href: '/termos' });

    // ---- contato ----
    const { phones, ctaPhone, ctaIsWhats, ctaHref } = contatosDoTenant(site, 4);
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const enderecoLinha = [site?.Dados?.endereco, [site?.Dados?.bairro, site?.Dados?.cidade].filter(Boolean).join(' - '), site?.Dados?.cep]
        .filter(Boolean)
        .join(', ');
    const redes = redesDoTenant(site);
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const empresa = site?.Dados?.empresa || '';
    const creci = site?.Dados?.creci;
    const cnpj = site?.Dados?.cnpj;
    const verImoveisHref = `/imoveis/${transacaoPrincipal}/-/-/-/-`;

    // ---- faixa contínua: cidades (com imóvel, quando a contagem vier) intercaladas com tipos ----
    const faixa: ItemFaixa[] = useMemo(() => {
        const urlLista = (cidade: string, tipo: string) =>
            removerAcentos(`/imoveis/${transacaoPrincipal}/${cidade ? encodeSegment(cidade, site?.id) : '-'}/-/-/${tipo ? encodeSegment(tipo, site?.id) : '-'}`)
                .toLowerCase()
                .replaceAll(' ', '-');
        const todasCidades = (Array.isArray(allCities) ? allCities : []).filter((c: any) => c?.label);
        const algumaComContagem = todasCidades.some((c: any) => Number(c?.imoveis) > 0);
        const cidades: ItemFaixa[] = todasCidades
            .filter((c: any) => !algumaComContagem || Number(c?.imoveis) > 0)
            .slice(0, 10)
            .map((c: any) => ({ label: String(c.label).trim(), href: urlLista(String(c.label).trim(), ''), tipo: 'cidade' as const }));
        const tipos: ItemFaixa[] = (Array.isArray(allCitiesTipes) ? allCitiesTipes : [])
            .map((t: any) => String(t?.nome || t?.label || '').trim())
            .filter(Boolean)
            .slice(0, 8)
            .map((nome: string) => ({ label: nome, href: urlLista('', nome), tipo: 'tipo' as const }));
        const mesclado: ItemFaixa[] = [];
        for (let i = 0; i < Math.max(cidades.length, tipos.length); i++) {
            if (cidades[i]) mesclado.push(cidades[i]);
            if (tipos[i]) mesclado.push(tipos[i]);
        }
        return mesclado;
    }, [allCities, allCitiesTipes, transacaoPrincipal]);
    // a faixa precisa cobrir telas largas: repete a lista até ~12 itens por cópia.
    const faixaCopia: ItemFaixa[] = useMemo(() => {
        if (!faixa.length) return [];
        const out: ItemFaixa[] = [];
        while (out.length < 12) out.push(...faixa);
        return out;
    }, [faixa]);
    const faixaRef = useRef<HTMLDivElement | null>(null);
    const faixaVisivel = useInView(faixaRef, { margin: '120px 0px' });
    // o degradê da chamada só "respira" com a chamada na tela.
    const chamadaRef = useRef<HTMLDivElement | null>(null);
    const chamadaVisivel = useInView(chamadaRef, { margin: '80px 0px' });
    const duracaoFaixa = `${Math.max(28, faixaCopia.length * 3.2)}s`;

    // ---- revelação das colunas ----
    const revela = (i: number) => ({
        initial: reduzido ? false : ({ opacity: 0, y: 32 } as const),
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.2 },
        transition: { duration: 0.8, delay: reduzido ? 0 : i * 0.09, ease: EASE_HORIZONTE },
    });

    // ---- voltar ao topo magnético: a ZONA (estática) mede; o botão (que se move) segue com mola ----
    const imaX = useMotionValue(0);
    const imaY = useMotionValue(0);
    const molaIma = { stiffness: 240, damping: 16, mass: 0.5 };
    const botaoX = useSpring(imaX, molaIma);
    const botaoY = useSpring(imaY, molaIma);
    const aoMoverIma = (e: React.PointerEvent<HTMLDivElement>) => {
        if (reduzido || e.pointerType !== 'mouse') return;
        const r = e.currentTarget.getBoundingClientRect();
        imaX.set((e.clientX - (r.left + r.width / 2)) * 0.45);
        imaY.set((e.clientY - (r.top + r.height / 2)) * 0.45);
    };
    const soltarIma = () => {
        imaX.set(0);
        imaY.set(0);
    };
    const voltarAoTopo = () => {
        trackMetaEvent(site, 'ViewContent', { content_name: 'Voltar ao topo', content_category: 'footer_navigation' });
        window.scrollTo({ top: 0, behavior: reduzido ? 'auto' : 'smooth' });
    };

    const varsRodape = {
        background: corFundo,
        color: corTexto,
        '--hz-fundo': corFundo,
        '--hz-acento': corAcento,
        '--hz-sutil': hexParaRgba(corTexto, 0.14),
        '--hz-sutil-forte': hexParaRgba(corTexto, 0.3),
        '--hz-cta': corCta,
        '--hz-cta-texto': corCtaTexto,
        '--hz-grad-a': `linear-gradient(100deg, ${g1} 0%, ${gMeio} 30%, ${g2} 55%, ${g1} 100%)`,
        '--hz-grad-b': `linear-gradient(100deg, ${g2} 0%, ${g1} 45%, ${gMeio} 70%, ${g2} 100%)`,
    } as CSSProperties;

    const renderLink = (item: ItemMenu, idx: number, cat: string) =>
        item.external ? (
            <li key={idx}>
                <a href={item.href} className={styles.link} rel="noopener noreferrer" onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </a>
            </li>
        ) : (
            <li key={idx}>
                <Link prefetch={false} href={withCodsite(item.href)} className={styles.link} onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </Link>
            </li>
        );

    const renderFaixa = (copia: number) => (
        <ul className={styles.faixaLista} aria-hidden={copia > 0 ? true : undefined}>
            {(reduzido ? faixa : faixaCopia).map((it, idx) => (
                <li key={`${copia}-${idx}`} className={styles.faixaItem}>
                    <Link
                        prefetch={false}
                        href={withCodsite(it.href)}
                        className={`${styles.faixaLink} ${it.tipo === 'tipo' ? styles.faixaTipo : ''}`}
                        tabIndex={copia > 0 ? -1 : undefined}
                        onClick={trackFooter(it.label, it.tipo === 'cidade' ? 'footer_city' : 'footer_type')}
                    >
                        {it.label}
                    </Link>
                    <span className={styles.faixaEstrela} aria-hidden="true">
                        ✦
                    </span>
                </li>
            ))}
        </ul>
    );

    return (
        <MotionConfig reducedMotion="user">
            {/* "Pesquisas mais populares" com o visual da HOME do modelo em todas as páginas (Daniel 15/10: no
                /empreendimento saía o bloco genérico). O estilo vive nas regras `.home` da home do modelo; a casca
                repete a classe (e as variáveis) aqui — na home fica aninhada na mesma classe, sem efeito. */}
            {!hidePopular && (
                <div className={hzHome.home}>
                    <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin} />
                </div>
            )}
            {/* BUILDER: âncora estável do bloco "rodape". */}
            <footer className={styles.footer} data-builder-section="rodape" style={varsRodape}>
                {/* ---------- chamada final ---------- */}
                <div className={styles.chamada} ref={chamadaRef}>
                    <motion.p className={styles.sobretitulo} {...revela(0)}>
                        <span className={styles.sobretituloLinha} aria-hidden="true" />
                        {empresa || 'Vamos conversar'}
                    </motion.p>
                    <motion.h2
                        className={`${styles.chamadaTitulo} ${chamadaVisivel && !reduzido ? styles.animando : ''}`}
                        data-texto={FRASE_CHAMADA}
                        {...revela(1)}
                    >
                        {FRASE_CHAMADA}
                    </motion.h2>
                    <motion.div className={styles.chamadaAcoes} {...revela(2)}>
                        {ctaPhone ? (
                            <a
                                className={styles.ctaPrincipal}
                                href={ctaHref}
                                rel="noopener noreferrer"
                                target={ctaIsWhats ? '_blank' : undefined}
                                title={ctaPhone.nome || (ctaIsWhats ? 'WhatsApp' : 'Telefone')}
                                onClick={() => {
                                    trackLead(site, null); // whatsapp_click no GTM/GA, como no rodapé clássico (sem imóvel = sem Meta)
                                    trackMetaEvent(site, 'Contact', {
                                        content_name: 'Chamada do rodapé',
                                        contact_method: ctaIsWhats ? 'whatsapp' : 'phone',
                                        content_category: 'footer_contact',
                                    });
                                }}
                            >
                                {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                {ctaIsWhats ? 'Falar no WhatsApp' : `Ligar ${MaskPhone(ctaPhone.tel || '')}`}
                            </a>
                        ) : null}
                        <Link prefetch={false} href={withCodsite(verImoveisHref)} className={styles.ctaSecundario} onClick={trackFooter('Ver imóveis', 'footer_navigation')}>
                            Ver imóveis
                            <IconeSeta />
                        </Link>
                    </motion.div>
                </div>

                {/* ---------- faixa contínua ---------- */}
                {faixa.length > 0 ? (
                    <nav className={styles.faixa} ref={faixaRef} aria-label="Imóveis por cidade e tipo">
                        <div
                            className={`${styles.faixaTrilho} ${reduzido ? styles.faixaParada : ''} ${faixaVisivel ? '' : styles.faixaPausada}`}
                            style={{ animationDuration: duracaoFaixa }}
                        >
                            {renderFaixa(0)}
                            {reduzido ? null : renderFaixa(1)}
                        </div>
                    </nav>
                ) : null}

                {/* ---------- grade ---------- */}
                <div className={styles.conteudo}>
                    <div className={`${styles.grade}${institucional.length ? '' : ` ${styles.gradeTres}`}`}>
                        <motion.div className={`${styles.col} ${styles.colMarca}`} {...revela(0)}>
                            {logoSrc ? (
                                <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                                    <Image src={logoSrc} alt={`Imobiliária ${empresa}`} className={styles.logo} width={220} height={80} unoptimized />
                                </Link>
                            ) : (
                                <span className={styles.logoTexto}>{empresa}</span>
                            )}
                            {empresa || creci ? (
                                <p className={styles.marcaLinha}>
                                    {empresa}
                                    {empresa && creci ? ' · ' : ''}
                                    {creci ? `CRECI ${creci}` : ''}
                                </p>
                            ) : null}
                            {redes.length > 0 ? (
                                <div className={styles.redes}>
                                    {redes.map((r) => {
                                        const Icone = ICONE_REDE[r.rede];
                                        return (
                                            <a
                                                key={r.rede}
                                                href={r.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.rede}
                                                title={`Navegar para o ${r.rede} da imobiliária`}
                                                aria-label={r.rede}
                                            >
                                                {Icone ? <Icone aria-hidden={true} /> : r.rede}
                                            </a>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </motion.div>

                        <motion.div className={styles.col} {...revela(1)}>
                            <span className={styles.colTitulo}>Navegação</span>
                            <ul className={styles.lista}>{navegacao.map((item, idx) => renderLink(item, idx, 'footer_navigation'))}</ul>
                        </motion.div>

                        <motion.div className={styles.col} {...revela(2)}>
                            <span className={styles.colTitulo}>Contato</span>
                            <ul className={styles.lista}>
                                {phones.map((p, idx) => {
                                    const href = p.whats ? linkWhatsAppDoTenant(site, p.tel) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                                    return (
                                        <li key={idx}>
                                            <a
                                                className={`${styles.link} ${styles.contato}`}
                                                href={href}
                                                rel="noopener noreferrer"
                                                target={p.whats ? '_blank' : undefined}
                                                onClick={() => {
                                                    trackLead(site, null); // whatsapp_click (GTM/GA), como no rodapé clássico
                                                    trackFooter('Telefone do rodapé', 'footer_contact')();
                                                }}
                                            >
                                                {p.whats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                                <span>
                                                    {MaskPhone(p.tel || '')}
                                                    {p.nome ? ` — ${p.nome}` : ''}
                                                </span>
                                            </a>
                                        </li>
                                    );
                                })}
                                {email ? (
                                    <li>
                                        {emailVisivel ? (
                                            <a className={styles.link} href={`mailto:${email}`}>
                                                {email}
                                            </a>
                                        ) : (
                                            <button type="button" className={`${styles.link} ${styles.verEmail}`} onClick={() => setEmailVisivel(true)}>
                                                Ver e-mail
                                            </button>
                                        )}
                                    </li>
                                ) : null}
                                {enderecoLinha ? <li className={styles.endereco}>{enderecoLinha}</li> : null}
                            </ul>
                        </motion.div>

                        {institucional.length ? (
                            <motion.div className={styles.col} {...revela(3)}>
                                <span className={styles.colTitulo}>Institucional</span>
                                <ul className={styles.lista}>{institucional.map((item, idx) => renderLink(item, idx, 'footer_institutional'))}</ul>
                            </motion.div>
                        ) : null}
                    </div>

                    {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup). */}
                    <FiliaisRodape site={site} fundo={corFundo} cor={corTexto} />

                    {/* SEO: o que o rodapé clássico lista e a faixa contínua não tem (empreendimentos e
                        cidades/tipos além do limite dela), em linha discreta (16/10) */}
                    <LinhaSeoRodape
                        site={site}
                        allCities={allCities}
                        allCitiesTipes={allCitiesTipes}
                        allRodape={allRodape}
                        withCodsite={withCodsite}
                        jaTem={[...faixa, ...navegacao, ...institucional, ...legais].map((i) => i.href)}
                    />
                </div>

                {/* ---------- linha final ---------- */}
                <div className={styles.linhaFinal} style={{ background: corBarra, color: corBarraTexto }}>
                    <div className={styles.linhaFinalInner}>
                        <div className={styles.linhaFinalTexto}>
                            <span className={styles.legal}>
                                © {new Date().getFullYear()} {empresa}. Todos os direitos reservados.
                                {creci ? ` · CRECI ${creci}` : ''}
                                {cnpj ? ` · CNPJ ${cnpj}` : ''}
                            </span>
                            <ul className={styles.legais}>{legais.map((item, idx) => renderLink(item, idx, 'footer_institutional'))}</ul>
                            {site?.html_rodape ? <span className={styles.htmlRodape} dangerouslySetInnerHTML={{ __html: site?.html_rodape }} /> : null}
                            {/* Selo ImobTotal: respeita copyright + EsconderRodapeSobressai (gate dentro do componente). */}
                            <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria fonte="13px" />
                        </div>
                        <div className={styles.imaZona} onPointerMove={aoMoverIma} onPointerLeave={soltarIma}>
                            <motion.button
                                type="button"
                                className={styles.voltarTopo}
                                style={{ x: botaoX, y: botaoY }}
                                onClick={voltarAoTopo}
                                aria-label="Voltar ao topo da página"
                                title="Voltar ao topo"
                                whileTap={{ scale: 0.92 }}
                            >
                                <IconeSeta cima />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </footer>
        </MotionConfig>
    );
};

class HorizonteFooter extends React.Component<RodapeSectionProps, { hasError: boolean }> {
    constructor(props: RodapeSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <HorizonteFooterInner {...this.props} />;
    }
}

export default HorizonteFooter;
